import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  GetDisclosureRequirementGroupsQuery_,
  useFlaggedTargetsCountQuery,
  useGetDisclosureRequirementGroupsQuery,
  useGetEsrsStandardHeaderQuery,
  useGetMfDisclosureRequirementGroupsQuery,
  useGetSubsidiariesDRsCountQuery,
} from 'models';
import { DREnums, FrameworkRefs, useMaterialStandardId } from 'containers/Esrs';
import { mfStandardRefs } from 'containers/Esrs/utils';

export const timeHorizonOptions = [
  { value: 'short-term', label: 'Short-term' },
  { value: 'medium-term', label: 'Medium-term' },
  { value: 'long-term', label: 'Long-term' },
];

type RemoveTypename<T> = T extends any[]
  ? RemoveTypename<T[number]>[]
  : T extends object
    ? {
        [K in keyof T as K extends '__typename' ? never : K]: RemoveTypename<T[K]>;
      }
    : T;

type DisclosureRequirementGroupsType = RemoveTypename<
  GetDisclosureRequirementGroupsQuery_['requirementGroup']
>;
type DisclosureRequirementType =
  GetDisclosureRequirementGroupsQuery_['requirementGroup'][number]['requirements'][number];

const mapRequirementGroups = (requirementGroups: DisclosureRequirementGroupsType) => {
  const iroTypes = [DREnums.action, DREnums.target];

  const keyTitleArray = [
    ...requirementGroups?.map((d) => {
      if (d.title === 'Metrics') {
        if (d.requirements.every((r) => r.metrics.length === 0)) return [];
        return [
          { title: d.title, key: d.reference, counter: 0, reference: d.reference },
          ...d.requirements
            .filter((r) => !!r.metrics.length)
            .map((r) => {
              return {
                title: r.title,
                key: `${r?.type}_${r.reference}`,
                reference: r.reference,
                type: r.type,
                counter: 0,
                actionsCount: r.actions_aggregate?.aggregate?.count,
                targetsCount: r.targets_aggregate?.aggregate?.count,
              };
            }),
        ];
      } else
        return [
          { title: d.title, key: d.reference, counter: 0, reference: d.reference },
          ...d.requirements
            .filter((req) => iroTypes.includes(req.type as DREnums) || req.metrics.length)
            .map((r) => {
              return {
                title: r.title,
                key: `${r?.type}_${r.reference}`,
                reference: r.reference,
                type: r.type,
                counter: 0,
                actionsCount: r.actions_aggregate?.aggregate?.count,
                targetsCount: r.targets_aggregate?.aggregate?.count,
              };
            }),
        ];
    }),
  ];

  return keyTitleArray;
};

export const useGetDRGroupData = () => {
  const { standardRef = '', esrsAssessmentId = '', framework = '' } = useParams();
  const isMFStandard = useMemo(() => mfStandardRefs.includes(standardRef), [standardRef]);

  const { data: standardData, loading: standardLoading } = useGetEsrsStandardHeaderQuery({
    variables: { reference: standardRef },
    skip: !standardRef,
  });
  const standard = useMemo(() => standardData?.esrsStandard, [standardData]);

  const {
    companyAssessmentId,
    parentAssessmentId,
    isDataGatheringOnly,
    loading: materialLoading,
  } = useMaterialStandardId(standardRef, esrsAssessmentId);

  const { data: flaggedTargets, loading: flaggedLoading } = useFlaggedTargetsCountQuery({
    variables: {
      esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const { data, loading } = useGetDisclosureRequirementGroupsQuery({
    variables: {
      standardRef,
      materialStandardId: companyAssessmentId,
      parentStandardId: parentAssessmentId || companyAssessmentId,
    },
    skip: !standardRef || !companyAssessmentId || isMFStandard,
  });

  const transformedData = useMemo(() => {
    return {
      requirementGroup: data?.requirementGroup?.map((group) => {
        return {
          ...group,
          requirements: group.requirements.map((req) => {
            return {
              ...req,
              metrics: [
                ...req.metrics,
                ...req.mfDisclosureRequirements.flatMap((mfDr) =>
                  mfDr.mfDisclosureRequirement.mfDisclosureRequirementItems.map(
                    (mfItem) => mfItem.metric
                  )
                ),
              ],
            };
          }),
        };
      }),
    };
  }, [data]);

  const { data: mfDRGroups, loading: mfDRLoading } = useGetMfDisclosureRequirementGroupsQuery({
    variables: {
      mfStandardRef: standardRef,
      materialStandardId: companyAssessmentId,
      parentStandardId: parentAssessmentId || companyAssessmentId,
    },
    skip: !standardRef || !companyAssessmentId || !isMFStandard,
  });

  const { data: subData, loading: subLoading } = useGetSubsidiariesDRsCountQuery({
    variables: {
      standardRef,
      esrsAssessmentId,
    },
    skip: !standardRef || !esrsAssessmentId,
  });

  const drGroups = useMemo(
    () =>
      isMFStandard
        ? {
            requirementGroup: mfDRGroups?.requirementGroup?.map((group) => ({
              ...group,
              requirements:
                group.requirements.map(
                  (req) =>
                    ({
                      ...req,
                      metrics: req.metrics.map((m) => m.metric),
                    }) as DisclosureRequirementType
                ) ?? [],
            })),
          }
        : transformedData,
    [isMFStandard, mfDRGroups, transformedData]
  );

  const requirementGroups = useMemo(() => {
    if (!drGroups?.requirementGroup) return [];

    return drGroups.requirementGroup
      .map((rg) => ({
        ...rg,
        requirements: rg.requirements.filter(
          (req) =>
            req.metrics.filter((m) => !m.childrenMetricsCount).length > 0 ||
            (framework === FrameworkRefs.esrs &&
              (req.type === DREnums.action ||
                req.type === DREnums.target ||
                req.type === DREnums.policy))
        ),
      }))
      .filter((rg) => rg.requirements.length > 0);
  }, [drGroups]);

  const sortedRequirements = useMemo(() => {
    const sortedData = mapRequirementGroups(requirementGroups ?? []);
    return sortedData;
  }, [requirementGroups]);

  const allRequirements = useMemo(
    () => drGroups?.requirementGroup?.flatMap((rg) => rg?.requirements ?? []) ?? [],
    [drGroups]
  );

  const allSubsidiariesStandard = useMemo(
    () => subData?.EsrsAssessment_by_pk?.subsidiaries.flatMap((s) => s.materialStandards) ?? [],
    [subData]
  );

  const targetsCount = useMemo(
    () =>
      allRequirements.find((req) => req.type === DREnums.target)?.targets_aggregate.aggregate
        ?.count ?? 0,
    [allRequirements]
  );

  const actionsCount = useMemo(
    () =>
      (allRequirements.find((req) => req.type === DREnums.action)?.actions_aggregate.aggregate
        ?.count ?? 0) +
      (allSubsidiariesStandard
        .flatMap((ms) => ms.actions_aggregate.aggregate?.count ?? 0)
        ?.reduce((a, b) => a + b, 0) ?? 0),
    [allRequirements, allSubsidiariesStandard]
  );

  const policiesCount = useMemo(
    () =>
      (allRequirements.find((req) => req.type === DREnums.policy)?.policies?.[0]?.attachmentBox
        ?.attachments?.length ?? 0) +
      (allSubsidiariesStandard
        .flatMap((ms) => ms.policies?.[0]?.attachmentBox?.attachments?.length ?? 0)
        ?.reduce((a, b) => a + b, 0) ?? 0),
    [allRequirements, allSubsidiariesStandard]
  );

  return {
    standard,
    isDataGatheringOnly,
    sortedRequirements,
    targetsCount,
    actionsCount,
    policiesCount,
    flaggedTargets: flaggedTargets?.esrs_Target_aggregate,
    isMFStandard,
    loading:
      loading || standardLoading || materialLoading || flaggedLoading || mfDRLoading || subLoading,
  };
};
